/**
 * Farmer home — web port of the mobile farmer landing (apps/mobile/app/farmer-home.tsx).
 *
 * This is what an INDIVIDUAL farmer (G1, `role='farmer'`) sees instead of the
 * coop-admin Dashboard. The orchestrator redirects role=farmer here on login and
 * adds it to the sidebar; routing/nav are wired centrally (this file owns the
 * page only).
 *
 * RESTRUCTURE (design-fidelity-tier1): the screen was a bare nav-launcher; the
 * Mango — Farmer Market design (Documentation/Claude design files/ui_kits/
 * farmer-app/screens/HomeScreen.jsx) makes Home a CONTENT dashboard:
 *   1. Greeting — farmer name (/me/profile) + coop/group affiliation
 *      (/me/farm-profile) as the membership chip.
 *   2. Earnings HERO — lifetime off-platform sales from /harvests/summary,
 *      labelled "Total earned" (NOT "this month"; the summary aggregates all
 *      sold/settled harvests). No on-chain settlement line (off-chain only).
 *   3. "Your crops on sale" — the farmer's own ACTIVE listings
 *      (/marketplace/listings?mine=true&status=active) as cards with a
 *      PLACEHOLDER gradient image tile (real photos are TD-135, out of scope).
 *   4. Offers nudge strip — driven by summing ListingDto.offerCount across the
 *      active listings; links to the listing-management surface.
 *   5. "Add listing" action — routes to My harvests (publish a harvest →
 *      listing) since publishing happens from a harvest's detail.
 *   6. Existing nav affordances (the action grid) are KEPT below the dashboard.
 *
 * WEATHER STRIP: intentionally OMITTED — there is no weather backend. See the
 * TODO below; if/when a weather service lands, the design's WeatherGreeting
 * header (temp / condition / wind / humidity) slots in above the greeting.
 *
 * Resilience: every section comes from its own query and fails OPEN — a failed
 * /me/profile falls back to a plain "Welcome back"; a failed /me/farm-profile
 * (404 for non-farmer accounts, or a blip) silently renders no banner; a failed
 * earnings or listings query renders a quiet inline fallback rather than
 * blocking the page. Nothing here gates the action grid.
 *
 * Cache note: the display-name query reuses the ['me-profile'] key the Profile
 * screen uses, and the farm-profile query reuses ['me-farm-profile'] (shared
 * with useFarmerSurfaces), so nothing double-fetches.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Icon,
  Money,
  Stack,
  Chip,
  StatusPill,
  Text,
  useColorMode,
  mdSysColor,
} from '@fse/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readCurrentRole } from '../api/client';
import { meControllerGetFarmProfile, meControllerGetProfile } from '../api/generated/me/me';
import type { MyFarmProfileDto } from '../api/generated/models';
import { useFarmerSurfaces } from '../api/useFarmerSurfaces';
import { listListings, microToZarp, type ListingDto } from '../api/marketplace';
import { getMyEarnings } from '../api/earnings';

// The MD3 system-colour bag for the active mode. `mdSysColor.light` and
// `.dark` share the same KEY shape but carry distinct string-literal VALUE
// types (`as const`), so a `mdSysColor[mode]` lookup is the UNION of both —
// hence the union type here (not `(typeof mdSysColor)['light']`, which the dark
// branch isn't assignable to).
type SysColors = (typeof mdSysColor)[keyof typeof mdSysColor];

// --------------------------------------------------------------------------
// Action grid definition. Title-key → route → description-key. Kept as data so
// the grid stays a single map() and new surfaces are a one-line addition. The
// title/description are i18n keys resolved through t() at render (TD-088).
// A Material Symbols ligature is paired with each surface to lead the card.
// --------------------------------------------------------------------------
interface NavCardDef {
  to: string;
  titleKey: string;
  descriptionKey: string;
  icon: string;
  /**
   * Data-aware visibility gate (mirrors the mobile farmer-home rule via
   * useFarmerSurfaces). `harvests`/`contributions` cards show only when the
   * corresponding surface is active for this farmer. Undefined = always shown.
   */
  gate?: 'harvests' | 'contributions';
  /** Restrict to "farmer-like" roles (`farmer` + `group_lead`): the backend now
   *  grants a group_lead READ access to their own harvests/insights + full KYC
   *  (TD-098), so these surfaces are visible to both. Hidden for everyone else
   *  (e.g. coop admins, who have their own dashboard) — My harvests (/my-harvests),
   *  Insights (/insights), Verification (/verification). NB: harvest MUTATIONS
   *  remain hidden for affiliated users (group_lead included) on the detail/
   *  my-harvests screens via the `!isInCoop` gate (TD-083) — read-only here. */
  farmerLikeOnly?: boolean;
}

// Curated quick-launch shortcuts only (IA cleanup, task 4). The sidebar
// (_app.tsx) is now the single source of truth for full navigation and account
// items live under Settings, so this home page is a LIGHT dashboard: just the
// highest-frequency daily actions. My harvests / My contributions keep their
// data-aware gate (only one is the farmer's active surface). Market is a shared
// daily action; Notifications is the inbox. The long tail of nav cards moved to
// the sidebar — no routes were removed.
const ACTION_CARDS: readonly NavCardDef[] = [
  {
    to: '/my-harvests',
    titleKey: 'home.card.myHarvests.title',
    descriptionKey: 'home.card.myHarvests.description',
    icon: 'eco',
    gate: 'harvests',
    farmerLikeOnly: true,
  },
  {
    to: '/my-contributions',
    titleKey: 'home.card.myContributions.title',
    descriptionKey: 'home.card.myContributions.description',
    icon: 'inventory_2',
    gate: 'contributions',
  },
  {
    to: '/market',
    titleKey: 'home.card.market.title',
    descriptionKey: 'home.card.market.description',
    icon: 'storefront',
  },
  {
    to: '/notifications',
    titleKey: 'home.card.notifications.title',
    descriptionKey: 'home.card.notifications.description',
    icon: 'notifications',
  },
];

export function HomeScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];

  // Greeting name — best-effort. Errors fall back to a name-less greeting and
  // never block the page (see header note). Shares the ['me-profile'] cache key
  // with the Profile screen.
  const profileQuery = useQuery({
    queryKey: ['me-profile'],
    queryFn: ({ signal }) => meControllerGetProfile(signal),
  });

  // Membership banner — fail-open. A 404 (non-farmer account) or a network
  // error renders NO banner rather than surfacing an error, so retry is off and
  // the render below guards on isSuccess.
  const farmProfileQuery = useQuery<MyFarmProfileDto, FseApiError>({
    queryKey: ['me-farm-profile'],
    queryFn: ({ signal }) => meControllerGetFarmProfile(signal),
    retry: false,
  });

  // NOTE: the lifetime/all-time total + this-month figure both now come from
  // /harvests/earnings below (the dedicated earnings rollup). The old
  // /harvests/summary fetch was the hero's only consumer on this page, so it was
  // removed when the hero switched to the earnings endpoint — /harvests/summary
  // is unchanged and still serves the Insights screen.
  //
  // Earnings hero PRIMARY figure — "earned this month". /harvests/earnings sums
  // settled marketplace orders + recorded off-platform harvest sales for the
  // caller, split into thisMonth + lifetime. Money is a PLAIN ZAR decimal STRING
  // (NOT micro-ZARP), so Number(...) → <Money> with no 1e6 division (same as the
  // /harvests/summary totalSalesAmount). Hand-rolled client (orval not regen'd
  // for this endpoint — TD-124). Fails open: a 404/blip renders the zero state.
  const earningsQuery = useQuery({
    queryKey: ['harvests-earnings'],
    queryFn: ({ signal }) => getMyEarnings(signal),
    retry: false,
  });

  // The farmer's own ACTIVE listings (seller view). `mine: true` scopes to the
  // caller; `status: 'active'` restricts to the "on sale" crops. Hand-rolled
  // client (orval not regenerated for /marketplace — TD-124), wrapped in our own
  // useQuery. Fails open: an error renders a quiet inline fallback.
  const listingsQuery = useQuery({
    queryKey: ['marketplace-listings', 'mine', 'active'],
    queryFn: ({ signal }) => listListings({ mine: true, status: 'active' }, signal),
    retry: false,
  });
  const listings = listingsQuery.data?.data ?? [];
  // Offers nudge is driven by summing offerCount across the active listings.
  const totalOffers = listings.reduce((sum, l) => sum + (l.offerCount ?? 0), 0);

  // Data-aware grid gating (TD-089 parity with mobile farmer-home): show My
  // harvests / My contributions per the farmer's coop-membership + history.
  // Shares the ['me-farm-profile'] cache key with the banner query above.
  const surfaces = useFarmerSurfaces();
  // "Farmer-like" = G1 individual farmer + G2 group lead. The backend now grants
  // a group_lead READ access to their own harvests/insights + full KYC (TD-098),
  // so the harvest/insights/verification cards show for both. (Harvest mutations
  // stay hidden for affiliated users downstream via the `!isInCoop` gate — TD-083.)
  const role = readCurrentRole();
  const isFarmerLike = role === 'farmer' || role === 'group_lead';
  const cards = ACTION_CARDS.filter((card) => {
    if (card.farmerLikeOnly && !isFarmerLike) return false;
    if (card.gate === 'harvests') return surfaces.showHarvests;
    if (card.gate === 'contributions') return surfaces.showContributions;
    return true;
  });

  const displayName = profileQuery.data?.displayName;
  const greeting = displayName
    ? t('home.greeting.named', { name: displayName })
    : t('home.greeting.plain');

  // TODO(weather): the design's WeatherGreeting header (temp / condition / wind
  // / humidity) is intentionally omitted — there is no weather backend/service
  // wired. When a weather provider lands, render the strip above this greeting.

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h1">
          {greeting}
        </Text>

        {/* Membership status — only rendered once the farm-profile query
            succeeds; errors (404 non-farmer / network) silently skip. A coop
            member shows the coop name; an informal-group member shows the group
            name; otherwise the "independent" chip. */}
        {farmProfileQuery.isSuccess ? (
          <Box sx={{ mt: 1 }}>
            {farmProfileQuery.data.cooperativeName !== null ? (
              <Chip
                kind="assist"
                color="success"
                leadingIcon="groups"
                label={t('home.membership.member', {
                  name: farmProfileQuery.data.cooperativeName,
                })}
              />
            ) : farmProfileQuery.data.groupName !== null ? (
              <Chip
                kind="assist"
                color="success"
                leadingIcon="groups"
                label={t('home.membership.group', {
                  name: farmProfileQuery.data.groupName,
                  defaultValue: 'Group: {{name}}',
                })}
              />
            ) : (
              <Chip
                kind="assist"
                color="neutral"
                leadingIcon="person"
                label={t('home.membership.independent')}
              />
            )}
          </Box>
        ) : null}
      </Box>

      {/* EARNINGS HERO — PRIMARY figure is "earned this month" (settled
          marketplace orders + recorded off-platform sales this calendar month),
          with the all-time total as a smaller secondary line. Tappable card
          (links to harvests). Tinted with the primary-container role so it reads
          in light AND dark; no on-chain settlement line. Both
          /harvests/earnings amounts are PLAIN ZAR decimal strings (not
          micro-ZARP), so Number() → <Money> with no 1e6 division. */}
      <EarningsHero
        thisMonthAmount={
          earningsQuery.isSuccess ? Number(earningsQuery.data.thisMonth.salesAmount) : 0
        }
        thisMonthSalesCount={
          earningsQuery.isSuccess ? earningsQuery.data.thisMonth.salesCount : 0
        }
        lifetimeAmount={
          earningsQuery.isSuccess ? Number(earningsQuery.data.lifetime.salesAmount) : 0
        }
        isPending={earningsQuery.isPending}
        isError={earningsQuery.isError}
        sys={sys}
        t={t}
      />

      {/* "Your crops on sale" — the farmer's own active listings + Add listing. */}
      {isFarmerLike ? (
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Text variant="title-large" as="h2">
              {t('home.listings.title', 'Your crops on sale')}
            </Text>
            {listingsQuery.isSuccess ? (
              <Text variant="label-medium" color="text.secondary">
                {t('home.listings.activeCount', {
                  count: listings.length,
                  defaultValue: '{{count}} active',
                })}
              </Text>
            ) : null}
          </Box>

          {/* Offers nudge strip — only when there are offers waiting. Tinted
              with the tertiary-container role (mode-aware). */}
          {totalOffers > 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.75,
                py: 1.25,
                bgcolor: sys.tertiaryContainer,
                color: sys.onTertiaryContainer,
                borderRadius: 2,
              }}
            >
              <Icon name="sell" size={20} color="inherit" />
              <Box sx={{ flex: 1 }}>
                <Text variant="body-medium" color="inherit">
                  {t('home.offers.nudge', {
                    count: totalOffers,
                    defaultValue: '{{count}} new offer waiting on your listings',
                    defaultValue_other: '{{count}} new offers waiting on your listings',
                  })}
                </Text>
              </Box>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate({ to: '/my-listings' })}
              >
                {t('home.offers.see', 'See')}
              </Button>
            </Box>
          ) : null}

          {listingsQuery.isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : listingsQuery.isError ? (
            // Fail-open: the rest of the dashboard still renders. A quiet inline
            // alert + retry rather than a blocking error page.
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => listingsQuery.refetch()}>
                  {t('home.listings.retry', 'Retry')}
                </Button>
              }
            >
              {t('home.listings.loadError', 'Could not load your listings.')}
            </Alert>
          ) : listings.length === 0 ? (
            <Card>
              <CardContent>
                <Stack spacing={1}>
                  <Text variant="title-medium" as="h3">
                    {t('home.listings.empty.title', 'No crops on sale yet')}
                  </Text>
                  <Text variant="body-medium" color="text.secondary">
                    {t(
                      'home.listings.empty.body',
                      'Open a harvest and publish it to the marketplace to start receiving offers from buyers.',
                    )}
                  </Text>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="add" size={18} color="inherit" />}
                      onClick={() => navigate({ to: '/my-harvests' })}
                    >
                      {t('home.listings.add', 'Add listing')}
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.25}>
              {listings.map((listing) => (
                <ListingRow key={listing.id} listing={listing} sys={sys} t={t} />
              ))}
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<Icon name="add" size={18} color="inherit" />}
                  onClick={() => navigate({ to: '/my-harvests' })}
                >
                  {t('home.listings.add', 'Add listing')}
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      ) : null}

      {/* Action grid — the existing nav affordances, kept. Wraps to as many
          columns as the viewport allows; each card flexes from a 260px basis. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {cards.map((card) => (
          <NavCard
            key={card.to}
            to={card.to}
            icon={card.icon}
            title={t(card.titleKey)}
            description={t(card.descriptionKey)}
          />
        ))}
      </Box>
    </Stack>
  );
}

/**
 * Earnings hero — PRIMARY figure is "earned this month"; the all-time total is a
 * smaller secondary line below. Tappable (links to harvests). Tinted with the
 * primary-container role so it reads in both light and dark. Both amounts are
 * already plain ZAR numbers (caller does Number(...salesAmount)); pending shows a
 * spinner, error a quiet "unavailable" line — never blocks.
 */
function EarningsHero({
  thisMonthAmount,
  thisMonthSalesCount,
  lifetimeAmount,
  isPending,
  isError,
  sys,
  t,
}: {
  thisMonthAmount: number;
  thisMonthSalesCount: number;
  lifetimeAmount: number;
  isPending: boolean;
  isError: boolean;
  sys: SysColors;
  t: TFunction;
}) {
  return (
    <Link to="/my-harvests" style={{ textDecoration: 'none', display: 'block' }}>
      <Box
        sx={{
          bgcolor: sys.primaryContainer,
          color: sys.onPrimaryContainer,
          borderRadius: 3,
          p: 2.5,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          transition: 'box-shadow 200ms ease-out',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text variant="label-medium" color="inherit">
            {t('home.earnings.thisMonth', 'Earned this month')}
          </Text>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            <Text variant="label-medium" color="inherit">
              {t('home.earnings.cta', 'See harvests')}
            </Text>
            <Icon name="arrow_forward" size={16} color="inherit" />
          </Box>
        </Box>

        {isPending ? (
          <Box sx={{ py: 0.5 }}>
            <CircularProgress size={28} color="inherit" />
          </Box>
        ) : (
          <Money amount={thisMonthAmount} size="xl" color={sys.onPrimaryContainer} weight={800} />
        )}

        {/* Lifetime total — smaller secondary line. Hidden while pending. */}
        {!isPending && !isError ? (
          <Box sx={{ opacity: 0.85 }}>
            <Text variant="body-small" color="inherit">
              {t('home.earnings.lifetime', {
                amount: lifetimeAmount.toLocaleString('en-ZA', {
                  style: 'currency',
                  currency: 'ZAR',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
                defaultValue: '{{amount}} all-time',
              })}
            </Text>
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
          <Icon name="payments" size={16} color="inherit" />
          <Text variant="body-medium" color="inherit">
            {isError
              ? t('home.earnings.unavailable', 'Earnings unavailable right now')
              : t('home.earnings.salesCount', {
                  count: thisMonthSalesCount,
                  defaultValue: '{{count}} sale this month',
                  defaultValue_other: '{{count}} sales this month',
                })}
          </Text>
        </Box>
      </Box>
    </Link>
  );
}

/**
 * Listing row — a crop the farmer has on sale. Mirrors the design's ListingCard
 * (Cards.jsx): a placeholder image tile (gradient + crop icon — real photos are
 * TD-135), the crop/product + grade + qty, the ask price per kg, and a status
 * pill. Tappable → the listing-management surface (/my-listings).
 */
function ListingRow({
  listing,
  sys,
  t,
}: {
  listing: ListingDto;
  sys: SysColors;
  t: TFunction;
}) {
  const askPerKg = microToZarp(listing.askPriceZarpPerKgMicro);
  return (
    <Link to="/my-listings" style={{ textDecoration: 'none', display: 'block' }}>
      <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* PLACEHOLDER image tile — gradient + crop icon (real photos
                are TD-135, out of scope). */}
            <Box
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: sys.onTertiaryContainer,
                background: `linear-gradient(135deg, ${sys.tertiaryContainer}, ${sys.secondaryContainer})`,
              }}
            >
              <Icon name="eco" size={28} color="inherit" />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Text variant="title-medium" as="span" truncate>
                {listing.product}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Text variant="body-small" color="text.secondary">
                  {[
                    listing.grade
                      ? t('home.listings.grade', {
                          grade: listing.grade,
                          defaultValue: 'Grade {{grade}}',
                        })
                      : null,
                    t('home.listings.qty', {
                      qty: listing.qtyKg,
                      defaultValue: '{{qty}} kg',
                    }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </Box>
            </Box>

            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Money amount={askPerKg} size="md" />
              <Box sx={{ mt: 0.25 }}>
                <Text variant="label-small" color="text.secondary">
                  {t('home.listings.perKg', '/ kg')}
                </Text>
              </Box>
            </Box>
          </Box>

          {/* Status + offers row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mt: 1.25,
              pl: { xs: 0, sm: '80px' },
            }}
          >
            <StatusPill variant={listing.status === 'active' ? 'active' : 'upcoming'}>
              {t(`home.listings.status.${listing.status}`, listing.status)}
            </StatusPill>
            {listing.offerCount > 0 ? (
              <StatusPill variant="due" icon="sell">
                {t('home.listings.offerCount', {
                  count: listing.offerCount,
                  defaultValue: '{{count}} offer',
                  defaultValue_other: '{{count}} offers',
                })}
              </StatusPill>
            ) : null}
          </Box>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * Tappable navigation card — a Link-wrapped Card with a leading icon, bold
 * title + muted description in the Mango — Farmer Market language. The flex
 * basis (260px) lets the parent flex-wrap container lay the cards out in a
 * responsive grid without a Grid component.
 */
function NavCard({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: string;
  title: string;
  description: string;
}) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  return (
    <Link to={to} style={{ textDecoration: 'none', flex: '1 1 260px', minWidth: 260 }}>
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          transition: 'box-shadow 200ms ease-out, border-color 200ms ease-out',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <CardContent>
          <Stack spacing={1}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: sys.secondaryContainer,
                color: sys.onSecondaryContainer,
              }}
            >
              <Icon name={icon} size={24} color="inherit" />
            </Box>
            <Text variant="title-medium">{title}</Text>
            <Text variant="body-medium" color="text.secondary">
              {description}
            </Text>
          </Stack>
        </CardContent>
      </Card>
    </Link>
  );
}
